import md5 from 'md5';
import config from 'App/babel/config';
import AppService from 'App/legacy/AppService';
import File from 'Resources/File';

export type Avatar = {
	url: string;
	gravatar: boolean;
	profileImg: boolean;
	initials: string;
};

export type AvatarUser = {
	id?: number;
	name?: string;
	email?: string;
} | null;

export type AvatarOptions = {
	skipProfile?: boolean;
	skipGravatarCall?: boolean;
	notFoundError?: boolean;
	size?: number | string;
};

// Shared across every consumer on purpose: without it a list of N avatars fires N File.find calls
// for the same user. Keyed by user id, holds the in-flight promise (stored before it resolves).
const cachedAvatars: Record<number, Promise<number | null | undefined> | undefined> = {};

const checkForProfileImg = (user: AvatarUser, opts: AvatarOptions): Promise<number | null | undefined> => {
	let profileId: number | null | undefined;

	if (opts.skipProfile || !user || !user.id) {
		return Promise.resolve(0);
	}

	const userId = user.id;
	const cached = cachedAvatars[userId];
	if (cached) {
		return cached;
	}

	profileId = AppService.getProfileImageId(userId);

	// 0 means "already looked up, this user has no profile image" — returning early on it is what
	// keeps us from re-querying File for every user without a picture.
	if (profileId || profileId === 0) {
		return Promise.resolve(profileId);
	}

	// Cache the promise before it resolves, so parallel avatars share the one in-flight request.
	const lookup = File.find({ entityId: userId, entity: 'ProfileImage' }).then((res: { data: { id: number }[] }) => {
		if (res.data.length) {
			profileId = res.data[0].id;
		}
		AppService.setProfileImageId(userId, profileId || 0);
		return profileId;
	});
	cachedAvatars[userId] = lookup;
	return lookup;
};

export const getInitials = (user: AvatarUser) => {
	if (!user || !user.name) {
		return '';
	}
	let i1 = '';
	let i2 = '';
	let nameArray: string[] = [];
	if (user.name) {
		// Spread instead of charAt so names starting with a surrogate pair keep their first glyph
		i1 = [...user.name.toString()][0];
		nameArray = user.name.split(' ');
		if (nameArray.length > 2) {
			i2 = [...nameArray[nameArray.length - 1]][0];
		} else if (nameArray.length === 2) {
			i2 = [...nameArray[1]][0];
		}
	}
	return (i1 || '') + (i2 || '');
};

export const getGravatar = (user: AvatarUser, options?: AvatarOptions) => {
	options = options || {};
	const size = options.size ? '&s=' + options.size : '';
	const notFoundQs = options.notFoundError ? '&d=404' : '';

	if (!user) {
		return {
			initials: '',
			url: 'https://secure.gravatar.com/avatar/xxx?d=mm' + size + notFoundQs,
			gravatar: true
		};
	}
	const initials = getInitials(user);
	let email = user.email || '';
	email = email.trim().toLowerCase();

	const emailHash = md5(email);
	let url;

	//Just for demo purposes
	const customerId = AppService.getCustomerId();
	if (customerId === 1) {
		const baseUrl = 'http://resource.upsales.com/3fdab54faac3ff9a9bb646d4f4b4424c/images/';
		switch (user.email) {
			case 'demo_2@upsales.com': //F
				url = baseUrl + 'womanone.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'demo_3@upsales.com': //F
				url = baseUrl + 'womantwo.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'demo_4@upsales.com': //M
				url = baseUrl + 'manone.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'demo_5@upsales.com': //F
				url = baseUrl + 'womantwo.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'demo_6@upsales.com': //F
				url = baseUrl + 'womanthree.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'demo_eng@upsales.com': //M
				url = baseUrl + 'mantwo.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'staffan@coor.se':
				url = baseUrl + 'staffan.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'ulrika@anticimex.se':
				url = baseUrl + 'ullrika.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'percy@abb.se':
				url = baseUrl + 'percy.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'peter@haglofs.se':
				url = baseUrl + 'peter.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'jan@ey.se':
				url = baseUrl + 'manthree.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'stefan@ey.se':
				url = baseUrl + 'stefanp.jpg';
				return { initials: initials, url: url, gravatar: true };

			case 'ahasmats@upsales.com':
				url = baseUrl + 'womanone.jpg';
				return { initials: initials, url: url, gravatar: true };

			case '':
				if (user.id === 1249) {
					url = baseUrl + 'peter.jpg';
				}
				if (user.id === 637) {
					url = baseUrl + 'manthree.jpg';
				}
				if (user.id === 638) {
					url = baseUrl + 'stefanp.jpg';
				}
				if (user.id === 1202) {
					url = baseUrl + 'ullrika.jpg';
				}
				if (user.id === 1249 || user.id === 637 || user.id === 638 || user.id === 1202) {
					return { initials: initials, url: url as string, gravatar: true };
				}
				break;
		}
	}

	url = 'https://secure.gravatar.com/avatar/' + emailHash + '?d=blank' + size + notFoundQs;
	return { initials: initials, url: url, gravatar: true };
};

export const getAvatar = (user: AvatarUser, opts?: AvatarOptions): Promise<Avatar> => {
	const options = opts || {};

	if (options.skipGravatarCall) {
		return Promise.resolve({
			url: '',
			gravatar: false,
			profileImg: false,
			initials: getInitials(user)
		});
	}
	return checkForProfileImg(user, options).then(profileId => {
		if (profileId) {
			const customerId = AppService.getCustomerId();
			return {
				url: `${config.URL}${config.API}${customerId}/resources/download/internal/${profileId}`,
				gravatar: true,
				profileImg: true,
				initials: getInitials(user)
			};
		} else {
			// getGravatar has never returned profileImg — keep it that way, consumers only read
			// url/initials/gravatar off it.
			return getGravatar(user, options) as Avatar;
		}
	});
};

export default { getAvatar, getGravatar, getInitials };
